import React, { useState, useEffect } from "react";
import "./HealthForm.css";
import axios from "axios";
import openNotification from "components/utils/CustomNotification";
import { FaSave } from "react-icons/fa";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

// --- Constants ---
const MAX_LIMITS = {
  height: 300,      // max 300 cm
  weight: 600,      // max 600 kg
  waist_size: 300,  // max 300 cm
  chest_size: 300,  // max 300 cm
};

interface UnitOption {
  value: string;
  label: string;
}

interface UnitMapData {
  weight_unit: UnitOption[];
  height_unit: UnitOption[];
  waist_size_unit: UnitOption[];
  chest_size_unit: UnitOption[];
  [key: string]: UnitOption[];
}

const HealthForm: React.FC = () => {
  useCheckSessionHeartBeat();
  const beAddress =
    (window as any)._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const API_URL_BODYMETRICS = `${beAddress}/api/get-individual-body-metrics`;
  const API_URL_UNIT_MAP =`${beAddress}/api/get-unit-map`;
  const API_SAVE_METRICS = `${beAddress}/api/body-measurements`;

  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const [unitMap, setUnitMap] = useState<UnitMapData | null>(null);

  const [formData, setFormData] = useState({
    height: "",
    weight: "",
    waist_size: "",
    chest_size: "",
    height_unit: "cm",
    weight_unit: "kg",
    waist_size_unit: "cm",
    chest_size_unit: "cm",
    from_datetime: "",
    to_datetime: "",
  });

  const [formErrors, setFormErrors] = useState({
    height: "",
    weight: "",
    waist_size: "",
    chest_size: "",
  });

  const formatDateToUTC = (date: Date | null): string | null => {
    return date ? date.toISOString().split("T")[0] + "T00:00:00Z" : null;
  };

  const hasValue = (val: any) => val !== null && val !== undefined && val !== "";

  const [showCustomModal, setShowCustomModal] = useState(false);

  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());


  // --- 1. Fetch Data ---
  useEffect(() => {
    const initData = async () => {
      try {
        const unitRes = await axios.get(API_URL_UNIT_MAP, {
          headers: {
            "user-uid": userUid,
            accept: "application/json"
          },
          withCredentials: true,
        });

        if (unitRes.status === 200 && unitRes.data.status === "Success") {
          setUnitMap(unitRes.data.data);
        }

        const toDate = new Date();
        const fromDate = new Date();
        const fromDateStr = formatDateToUTC(fromDate)!;
        const toDateStr = formatDateToUTC(toDate)!;

        const metricsRes = await axios.get(`${API_URL_BODYMETRICS}`, {
          params: { from_datetime: fromDateStr, to_datetime: toDateStr },
          headers: { "user-uid": userUid, accept: "application/json" },
          withCredentials: true,
        });

        if (metricsRes.status === 200 && metricsRes.data.status === "Success") {
          const dataList = metricsRes.data.data?.data || [];
          if (dataList.length > 0) {
            const latestEntry = dataList[dataList.length - 1];
            const newFormData: any = {
              from_datetime: fromDateStr,
              to_datetime: toDateStr,
            };

            const mapField = (field: string, unitField: string) => {
              const item = latestEntry.metrics.find((m: any) => m[field] !== undefined);
              if (item) {
                newFormData[field] = item[field];
                if (item.unit) newFormData[unitField] = item.unit;
              }
            };

            mapField("height", "height_unit");
            mapField("weight", "weight_unit");
            mapField("waist_size", "waist_size_unit");
            mapField("chest_size", "chest_size_unit");

            setFormData(prev => ({ ...prev, ...newFormData }));
          }
        }
      } catch (err) {
        console.error("Error initializing data", err);
      }
    };

    if (sessionUid && userUid) {
      initData();
    }
  }, [sessionUid, userUid, API_URL_BODYMETRICS, API_URL_UNIT_MAP]);

  // --- 2. Conversion Logic ---
  const convertToBaseUnit = (value: string, unit: string, type: "height" | "weight" | "size") => {
    const num = Number(value);
    if (isNaN(num)) return 0;

    if (type === "height") {
      if (unit === "feet" || unit === "ft") return num * 30.48;
      if (unit === "inch" || unit === "in") return num * 2.54;
      if (unit === "meters" || unit === "m") return num * 100;
      return num;
    }
    if (type === "weight") {
      if (unit === "lb" || unit === "lbs" || unit === "pound") return num / 2.20462;
      return num;
    }
    if (type === "size") {
      if (unit === "inch" || unit === "in") return num * 2.54;
      return num;
    }
    return num;
  };

  const convertValueForDisplay = (val: number, oldUnit: string, newUnit: string, type: string) => {
    const baseVal = convertToBaseUnit(val.toString(), oldUnit, type as any);

    if (type === "height") {
      if (newUnit === "feet" || newUnit === "ft") return baseVal / 30.48;
      if (newUnit === "inch" || newUnit === "in") return baseVal / 2.54;
      if (newUnit === "meters" || newUnit === "m") return baseVal / 100;
    }
    if (type === "weight") {
      if (newUnit === "lb" || newUnit === "lbs") return baseVal * 2.20462;
    }
    if (type === "size") {
      if (newUnit === "inch" || newUnit === "in") return baseVal / 2.54;
    }
    return baseVal;
  };

  // --- 3. Handlers ---
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (Number(value) < 0) return;

    const unitKey = name === "weight" || name === "height" ? `${name}_unit` : `${name}_unit`;
    const actualUnitKey = name === "waist_size" ? "waist_size_unit" : name === "chest_size" ? "chest_size_unit" : unitKey;
    const currentUnit = formData[actualUnitKey as keyof typeof formData];

    let type: "height" | "weight" | "size" = "size";
    if (name === "height") type = "height";
    if (name === "weight") type = "weight";

    const baseValue = convertToBaseUnit(value, currentUnit, type);
    const maxLimit = MAX_LIMITS[name as keyof typeof MAX_LIMITS];

    if (baseValue > maxLimit) {
      return;
    }

    setFormData({ ...formData, [name]: value });
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value: newUnit } = e.target;
    const fieldName = name.replace("_unit", "");
    const currentVal = Number(formData[fieldName as keyof typeof formData]);
    const oldUnit = formData[name as keyof typeof formData];

    let type = "size";
    if (fieldName === "height") type = "height";
    if (fieldName === "weight") type = "weight";

    const convertedVal = convertValueForDisplay(currentVal, oldUnit, newUnit, type);

    setFormData({
      ...formData,
      [name]: newUnit,
      [fieldName]: convertedVal ? parseFloat(convertedVal.toFixed(2)).toString() : ""
    });
  };

  // --- FIXED: Handle Single Field Save (Allow 0) ---
  // const handleSingleSave = async (field: string) => {
  //   const val = formData[field as keyof typeof formData];

  //   // Validate: Check if Empty String. 
  //   // REMOVED "Number(val) === 0" check to allow 0 as valid input.
  //   if (val === "" || val === undefined || val === null) {
  //     setFormErrors(prev => ({ ...prev, [field]: "Required" }));
  //     return;
  //   }

  //   const requestBody = {
  //     height: convertToBaseUnit(formData.height, formData.height_unit, "height"),
  //     weight: convertToBaseUnit(formData.weight, formData.weight_unit, "weight"),
  //     waist_size: convertToBaseUnit(formData.waist_size, formData.waist_size_unit, "size"),
  //     chest_size: convertToBaseUnit(formData.chest_size, formData.chest_size_unit, "size"),
  //     from_datetime: formData.from_datetime || formatDateToUTC(new Date()),
  //     to_datetime: formData.to_datetime || formatDateToUTC(new Date()),
  //   };

  //   console.log("height is ", formData.height)
  //   console.log("weight is ", formData.weight)
  //   console.log("waist is ", formData.waist_size)
  //   console.log("chest size ", formData.chest_size)

  //   try {
  //     const response = await axios.post(API_SAVE_METRICS, requestBody, {
  //       headers: { "user-uid": userUid },withCredentials: true,
  //     });
  //     if (response.status === 200 && response.data.status === "Success") {
  //       openNotification({ type: "success", message: `${field.replace("_", " ")} saved successfully!` });
  //     } else {
  //       openNotification({ type: "error", message: response.data.data });
  //     }
  //   } catch (error) {
  //     openNotification({ type: "error", message: "Error saving data." });
  //   }
  // };


  const handleSingleSave = async (field: string) => {
    const val = formData[field as keyof typeof formData];

    // Validate empty
    if (val === "" || val === undefined || val === null) {
      setFormErrors(prev => ({ ...prev, [field]: "Required" }));
      return;
    }

    let type: "height" | "weight" | "size" = "size";
    if (field === "height") type = "height";
    if (field === "weight") type = "weight";

    const unitKey =
      field === "waist_size"
        ? "waist_size_unit"
        : field === "chest_size"
        ? "chest_size_unit"
        : `${field}_unit`;

    const convertedValue = convertToBaseUnit(
      val.toString(),
      formData[unitKey as keyof typeof formData] as string,
      type
    );

    const requestBody: any = {
      [field]: convertedValue,
      from_datetime: formData.from_datetime || formatDateToUTC(new Date()),
      to_datetime: formData.to_datetime || formatDateToUTC(new Date()),
    };

    try {
      const response = await axios.post(API_SAVE_METRICS, requestBody, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });

      if (response.status === 200 && response.data.status === "Success") {
        openNotification({
          type: "success",
          message: `${field.replace("_", " ")} saved successfully!`,
        });
      } else {
        openNotification({ type: "error", message: response.data.data });
      }
    } catch (error) {
      openNotification({ type: "error", message: "Error saving data." });
    }
  };

  const handleCustomSave = async () => {
    if (changedFields.size === 0) {
      // openNotification({
      //   type: "info",
      //   message: "No changes to save",
      // });
      return;
    }

    const payload: any = {
      from_datetime: formData.from_datetime || formatDateToUTC(new Date()),
      to_datetime: formData.to_datetime || formatDateToUTC(new Date()),
    };

    changedFields.forEach((field) => {
      let type: "height" | "weight" | "size" = "size";
      if (field === "height") type = "height";
      if (field === "weight") type = "weight";

      const unitKey =
        field === "waist_size"
          ? "waist_size_unit"
          : field === "chest_size"
          ? "chest_size_unit"
          : `${field}_unit`;

      payload[field] = convertToBaseUnit(
        formData[field as keyof typeof formData].toString(),
        formData[unitKey as keyof typeof formData] as string,
        type
      );
    });

    try {
      const res = await axios.post(API_SAVE_METRICS, payload, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });

      if (res.status === 200 && res.data.status === "Success") {
        openNotification({
          type: "success",
          message: "Body metrics saved successfully",
        });
        setChangedFields(new Set());
        setShowCustomModal(false);
      } else {
        openNotification({ type: "error", message: res.data.data });
      }
    } catch (err) {
      openNotification({ type: "error", message: "Error saving data" });
    }
  };


  const isValidByUnit = (
      value: string,
      unit: string,
      field: "height" | "weight" | "waist_size" | "chest_size"
    ) => {
      if (value === "" || isNaN(Number(value))) return true;

      let type: "height" | "weight" | "size" = "size";
      if (field === "height") type = "height";
      if (field === "weight") type = "weight";

      const baseValue = convertToBaseUnit(value, unit, type);
      const maxLimit = MAX_LIMITS[field];

      return baseValue <= maxLimit;
    };



  return (
    <div className="health-form-container">
      <div className="healthform-header">
        <h2 className="healthformh2">Track Your Body Metrics</h2>
        {/* <button className="healthform-custom-btn">Custom</button> */}
        <button
          className="healthform-custom-btn"
          onClick={() => {
            setChangedFields(new Set());
            setShowCustomModal(true);
          }}
          >
          Custom
        </button>
      </div>

      <div className="healthform-form">
        {["height", "weight", "waist_size", "chest_size"].map((field) => {
          const label = field.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

          const unitKey = field === "weight" || field === "height" ? `${field}_unit` : `${field}_unit`;
          const apiUnitKey = field === "waist_size" ? "waist_size_unit"
            : field === "chest_size" ? "chest_size_unit"
              : `${field}_unit`;

          const options = unitMap ? unitMap[apiUnitKey] : [];
          const currentUnit = formData[unitKey as keyof typeof formData];
          const currentValue = formData[field as keyof typeof formData];
          const error = formErrors[field as keyof typeof formErrors];

          return (
            // <div className="card" key={field}>
            <div
              className={`card ${["height", "weight"].includes(field) ? "card-top-margin" : ""}`}
              key={field}
            >
              <div className="HealthForm-label">
                <label>{label}</label>
              </div>

              <div className="HealthForm-input">

              {/* INPUT + UNIT ROW */}
              <div className="healthform-input-row">
                <input
                  type="number"
                  step="0.01"
                  className="healthform-input-field"
                  name={field}
                  placeholder="0"
                  value={currentValue}
                  onChange={handleChange}
                />

                <select
                  className="healthform-select-field"
                  name={unitKey}
                  value={currentUnit}
                  onChange={handleUnitChange}
                >
                  {(!options || options.length === 0) && (
                    <option value={currentUnit}>{currentUnit}</option>
                  )}
                  {options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* SAVE BUTTON */}
              <div className="healthform-save-row">
                <button
                  type="button"
                  className="healthform-save-btn"
                  onClick={() => handleSingleSave(field)}
                >
                  Save
                </button>
              </div>

              {error && (
                <div className="healthform-error">
                  {error}
                </div>
              )}
            </div>
               
            </div>
          );
        })}
      </div>

      {showCustomModal && (
        <div className="healthform-modal-overlay">
          <div className="healthform-modal" role="dialog">

            {/* Close Icon */}
            <span
              className="healthform-modal-close"
              onClick={() => {
                setChangedFields(new Set());
                setShowCustomModal(false);
              }}
            >
              ✕
            </span>

            <h3 className="healthform-modal-title">Custom Body Metrics</h3>

            {/* Dates */}
            {/* <div className="healthform-modal-row">
              <div>
                <label>From Date</label>
                <input
                  type="date"
                  value={formData.from_datetime?.split("T")[0] || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      from_datetime: `${e.target.value}T00:00:00Z`,
                    })
                  }
                />
              </div>

              <div>
                <label>To Date</label>
                <input
                  type="date"
                  value={formData.to_datetime?.split("T")[0] || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      to_datetime: `${e.target.value}T00:00:00Z`,
                    })
                  }
                />
              </div>
            </div> */}

            <div className="healthform-modal-row healthform-date-row">
              <div>
                <label>From Date</label>
                <input
                  type="date"
                  value={formData.from_datetime?.split("T")[0] || ""}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      from_datetime: `${e.target.value}T00:00:00Z`,
                    });
                    setChangedFields(prev => new Set(prev).add("from_datetime"));
                  }}
                />
              </div>

              <div>
                <label>To Date</label>
                <input
                  type="date"
                  value={formData.to_datetime?.split("T")[0] || ""}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      to_datetime: `${e.target.value}T00:00:00Z`,
                    });
                    setChangedFields(prev => new Set(prev).add("to_datetime"));
                  }}
                />
              </div>
          </div>


            {/* Metric Fields */}
            {["height", "weight", "waist_size", "chest_size"].map((field) => {
              const unitKey =
                field === "waist_size"
                  ? "waist_size_unit"
                  : field === "chest_size"
                  ? "chest_size_unit"
                  : `${field}_unit`;

              const options = unitMap ? unitMap[unitKey] : [];

              return (
                <div className="healthform-modal-row" key={field}>
                  <label>{field.replace("_", " ").toUpperCase()}</label>
                  <div className="healthform-modal-input-row">
                    {/* <input
                      type="number"
                      step="0.01"
                      value={formData[field as keyof typeof formData]}
                      onChange={(e) => {
                        setFormData({ ...formData, [field]: e.target.value });
                        setChangedFields(prev => new Set(prev).add(field));
                      }}
                    /> */}
                    <input
                      type="number"
                      step="0.01"
                      value={formData[field as keyof typeof formData]}
                      onChange={(e) => {
                        const value = e.target.value;
                        const unitKey =
                          field === "waist_size"
                            ? "waist_size_unit"
                            : field === "chest_size"
                            ? "chest_size_unit"
                            : `${field}_unit`;

                        const unit = formData[unitKey as keyof typeof formData] as string;

                        if (!isValidByUnit(value, unit, field as any)) {
                          return;
                        }

                        setFormData({ ...formData, [field]: value });
                        setChangedFields(prev => new Set(prev).add(field));
                      }}
                    />
                    {/* <select
                      value={formData[unitKey as keyof typeof formData]}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          [unitKey]: e.target.value,
                        });
                        setChangedFields(prev => new Set(prev).add(field));
                      }}                      
                    > */}
                    <select
                      value={formData[unitKey as keyof typeof formData]}
                      onChange={(e) => {
                        const newUnit = e.target.value;
                        const currentValue = formData[field as keyof typeof formData] as string;

                        if (
                          currentValue &&
                          !isValidByUnit(currentValue, newUnit, field as any)
                        ) {
                          return;
                        }

                        setFormData({
                          ...formData,
                          [unitKey]: newUnit,
                        });
                        setChangedFields(prev => new Set(prev).add(field));
                      }}
                    >

                      {options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}

            {/* Buttons */}
            <div className="healthform-modal-actions">
              {/* <button
                className="healthform-save-btn"
                onClick={() => {
                  ["height", "weight", "waist_size", "chest_size"].forEach(
                    handleSingleSave
                  );
                  setShowCustomModal(false);
                }}
              >
                Save
              </button> */}

              {changedFields.size > 0 && (
                <button
                  className="healthform-save-btn"
                  onClick={handleCustomSave}
                >
                  Save
                </button>
              )}

              <button
                className="healthform-cancel-btn"
                onClick={() => {
                  setChangedFields(new Set());
                  setShowCustomModal(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default HealthForm;